import React from "react";
import { Lightbulb, Check, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MIN_SIDE = 600;

export async function lowResFiles(files) {
  const sizes = await Promise.all(files.map(async (f) => {
    try {
      const bmp = await createImageBitmap(f);
      const s = { name: f.name, w: bmp.width, h: bmp.height };
      bmp.close?.();
      return s;
    } catch {
      return null;
    }
  }));
  return sizes.filter((s) => s && Math.min(s.w, s.h) < MIN_SIDE);
}

export const readingIsUnclear = (card) => {
  const all = [card?.text || "", ...(card?.rules || []).map((r) => r.text || "")].join(" ");
  return /\[unreadable\]/i.test(all) || !(card?.name || "").trim();
};

function MockCard({ className = "", style }) {
  return (
    <div className={`absolute rounded-sm bg-slate-200 border border-slate-400 p-1 ${className}`} style={style}>
      <div className="h-1.5 w-3/4 bg-slate-600 rounded-sm mb-1" />
      <div className="flex gap-0.5 mb-1">{[0, 1, 2, 3].map((i) => <div key={i} className="h-1.5 w-1.5 bg-cyan-600 rounded-full" />)}</div>
      {[90, 70, 85, 60, 80].map((w) => <div key={w} className="h-1 bg-slate-500 rounded-sm mb-0.5" style={{ width: `${w}%` }} />)}
    </div>
  );
}

const EXAMPLES = [
  { id: "good", good: true, title: "One card, sharp, straight on", body: <MockCard className="inset-x-6 inset-y-2" /> },
  { id: "overlap", title: "Cards stacked on each other", body: <><MockCard className="left-3 top-2 w-14 h-16" /><MockCard className="left-9 top-5 w-14 h-16 shadow-lg" /></> },
  { id: "blurry", title: "Blurry or tiny", body: <MockCard className="inset-x-10 inset-y-5" style={{ filter: "blur(1.5px)" }} /> },
  { id: "cutoff", title: "Text cut off at the edge", body: <MockCard className="left-6 -top-4 w-16 h-24" /> },
  { id: "glare", title: "Photo with glare or tilt", body: <><MockCard className="inset-x-6 inset-y-2 rotate-12" /><div className="absolute inset-0 bg-gradient-to-br from-white/80 via-transparent to-transparent" /></> },
];

function Example({ ex }) {
  return (
    <figure className="w-28" data-testid={`screenshot-tip-example-${ex.id}`}>
      <div className={`relative h-20 overflow-hidden rounded border-2 bg-slate-800 ${ex.good ? "border-emerald-500" : "border-rose-500/70"}`}>{ex.body}</div>
      <figcaption className="mt-1 flex items-start gap-1 text-[11px] leading-tight">
        {ex.good ? <Check className="w-3 h-3 text-emerald-400 shrink-0" /> : <X className="w-3 h-3 text-rose-400 shrink-0" />}
        <span className={ex.good ? "text-emerald-300" : "text-muted-foreground"}>{ex.title}</span>
      </figcaption>
    </figure>
  );
}

export function ScreenshotTips({ reason }) {
  return (
    <div className="rounded-md border border-amber-700/50 bg-amber-950/20 p-3 space-y-2" data-testid="screenshot-tips">
      <p className="text-xs flex items-center gap-1.5 text-amber-200">
        <Lightbulb className="w-3.5 h-3.5" /> {reason || "Parts of this card were hard to read."} A cleaner screenshot usually fixes it:
      </p>
      <div className="flex flex-wrap gap-3">{EXAMPLES.map((ex) => <Example key={ex.id} ex={ex} />)}</div>
      <p className="text-[11px] text-muted-foreground">
        Long card? Scroll and take several screenshots that overlap a little, then select them together — repeated lines are merged, not doubled.
      </p>
    </div>
  );
}

export function LowResWarning({ small, onContinue, onCancel }) {
  return (
    <div className="rounded-md border border-amber-700/50 bg-amber-950/20 p-3 space-y-2 mt-2" data-testid="lowres-warning">
      <p className="text-xs flex items-start gap-1.5 text-amber-200">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          {small.length === 1 ? "This image looks" : `${small.length} images look`} small ({small.map((s) => `${s.w}×${s.h}`).join(", ")} px) — small icons and text may be misread.
          Screenshots at least {MIN_SIDE}px on the short side work best.
        </span>
      </p>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" onClick={onContinue} data-testid="lowres-continue-btn">Read anyway</Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} data-testid="lowres-cancel-btn">Choose other images</Button>
      </div>
    </div>
  );
}
